import {
  DAILY_LIMIT_MAX,
  DAILY_LIMIT_MIN,
  GRAMS_MAX,
  KCAL_PER_100G_MAX,
  LABEL_MAX,
  MIN_PASSWORD_LENGTH,
  NAME_MAX,
  NOTE_MAX,
  QUANTITY_MAX,
  QUESTION_MAX,
  WEIGHT_MAX,
  WEIGHT_MIN,
} from '@iac/contracts';
import type { ErrorCode } from '@iac/contracts';
import { currentLocale } from '../lib/locale';
import type { Dictionary } from './dictionary';

/**
 * What the user reads for each API error code, plus `network` for a request
 * that never got an answer. Typed against `ERROR_CODES`, so a code added to the
 * contract without a text in both languages fails the typecheck.
 */
type ErrorTexts = Record<ErrorCode, string> & { network: string };

const pl: ErrorTexts = {
  network: 'Brak połączenia z serwerem. Spróbuj ponownie.',
  invalid_request: 'Nieprawidłowe żądanie.',

  invalid_credentials: 'Nieprawidłowy login lub hasło.',
  not_authenticated: 'Sesja wygasła. Zaloguj się ponownie.',
  app_access_required: 'Nie masz dostępu do tej aplikacji.',
  wrong_current_password: 'Obecne hasło jest nieprawidłowe.',
  current_password_required: 'Podaj obecne hasło.',
  password_too_short: `Hasło musi mieć co najmniej ${MIN_PASSWORD_LENGTH} znaków.`,
  passwords_mismatch: 'Hasła nie są takie same.',

  user_not_found: 'Nie znaleziono użytkownika.',
  login_required: 'Podaj login.',
  login_taken: 'Użytkownik o tym loginie już istnieje.',
  cannot_delete_self: 'Nie możesz usunąć własnego konta.',

  link_not_found: 'Nie znaleziono linku.',
  label_required: `Podaj etykietę (maks. ${LABEL_MAX} znaków).`,
  url_invalid: 'Podaj prawidłowy adres http(s).',

  wallet_not_found: 'Nie znaleziono portfela.',
  goal_not_found: 'Nie znaleziono celu.',
  name_required: `Podaj nazwę (maks. ${NAME_MAX} znaków).`,
  amount_invalid: 'Kwota musi być liczbą ≥ 0.',
  target_amount_invalid: 'Kwota docelowa musi być większa od 0.',
  month_required: 'Podaj miesiąc docelowy (RRRR-MM).',

  note_not_found: 'Nie znaleziono notatki.',
  note_required: `Wpisz notatkę (maks. ${NOTE_MAX} znaków).`,
  question_required: `Wpisz pytanie (maks. ${QUESTION_MAX} znaków).`,
  brain_unreachable: 'Brain jest teraz niedostępny. Spróbuj za chwilę.',

  entry_not_found: 'Nie znaleziono produktu.',
  product_not_found: 'Nie znaleziono produktu w katalogu.',
  meal_not_found: 'Nie znaleziono sekcji.',
  duplicate_product: 'Ta nazwa jest już w katalogu (produkt lub danie).',
  dish_not_found: 'Nie znaleziono dania w katalogu.',
  duplicate_dish: 'Ta nazwa jest już w katalogu (produkt lub danie).',
  quantity_below_one: 'Ilość nie może spaść do zera — usuń produkt.',
  quantity_at_max: 'Osiągnięto maksymalną ilość.',
  meals_stale: 'Lista sekcji jest nieaktualna — odśwież stronę.',
  product_name_required: `Podaj nazwę produktu (maks. ${NAME_MAX} znaków).`,
  meal_name_required: `Podaj nazwę sekcji (maks. ${NAME_MAX} znaków).`,
  grams_invalid: `Gramy muszą być liczbą całkowitą od 1 do ${GRAMS_MAX}.`,
  kcal_invalid: `kcal/100g musi być liczbą całkowitą od 0 do ${KCAL_PER_100G_MAX}.`,
  quantity_invalid: `Ilość musi być liczbą od 0,01 do ${QUANTITY_MAX} (najwyżej 2 miejsca po przecinku).`,
  daily_limit_invalid: `Limit musi być liczbą całkowitą od ${DAILY_LIMIT_MIN} do ${DAILY_LIMIT_MAX}.`,
  day_invalid: 'Podaj datę (RRRR-MM-DD).',
  weight_invalid: `Waga musi być liczbą od ${WEIGHT_MIN} do ${WEIGHT_MAX} kg, z najwyżej jednym miejscem po przecinku.`,
  weight_not_found: 'Nie znaleziono pomiaru.',
  weight_date_taken: 'Ten dzień ma już pomiar wagi.',
  recipe_not_found: 'Nie znaleziono przepisu.',
  duplicate_recipe: 'Masz już przepis o tej nazwie.',
  checklist_not_found: 'Nie znaleziono listy.',
  checklist_item_not_found: 'Nie znaleziono pozycji na liście.',
  duplicate_checklist: 'Masz już listę o tej nazwie.',
  checklist_child_invalid:
    'Do listy łączonej można dodać tylko swoje zwykłe listy.',
  checklist_children_required: 'Wybierz co najmniej jedną listę.',
  checklist_group_not_found: 'Nie znaleziono grupy.',
  duplicate_checklist_group: 'Masz już grupę o tej nazwie.',
  checklist_group_not_empty:
    'W grupie są listy — przenieś je najpierw do innej grupy.',
  checklist_group_invalid: 'Listę można zapisać tylko w swojej grupie.',
  checklist_order_stale: 'Kolejność jest nieaktualna — odśwież stronę.',
};

const en: ErrorTexts = {
  network: 'Could not reach the server. Try again.',
  invalid_request: 'Invalid request.',

  invalid_credentials: 'Invalid login or password.',
  not_authenticated: 'Your session has expired. Sign in again.',
  app_access_required: "You don't have access to this app.",
  wrong_current_password: 'Current password is incorrect.',
  current_password_required: 'Current password is required.',
  password_too_short: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  passwords_mismatch: 'Passwords do not match.',

  user_not_found: 'User not found.',
  login_required: 'Login is required.',
  login_taken: 'A user with this login already exists.',
  cannot_delete_self: 'You cannot delete your own account.',

  link_not_found: 'Link not found.',
  label_required: `Label is required (max ${LABEL_MAX} chars).`,
  url_invalid: 'URL must be a valid http(s) address.',

  wallet_not_found: 'Wallet not found.',
  goal_not_found: 'Goal not found.',
  name_required: `Name is required (max ${NAME_MAX} chars).`,
  amount_invalid: 'Amount must be a number ≥ 0.',
  target_amount_invalid: 'Target amount must be greater than 0.',
  month_required: 'Target month is required (YYYY-MM).',

  note_not_found: 'Note not found.',
  note_required: `A note is required (max ${NOTE_MAX} chars).`,
  question_required: `A question is required (max ${QUESTION_MAX} chars).`,
  brain_unreachable:
    'The brain is unreachable right now. Try again in a moment.',

  entry_not_found: 'Entry not found.',
  product_not_found: 'Product not found in the catalog.',
  meal_not_found: 'Section not found.',
  duplicate_product:
    'This name is already in the catalog (a product or a dish).',
  dish_not_found: 'Dish not found in the catalog.',
  duplicate_dish: 'This name is already in the catalog (a product or a dish).',
  quantity_below_one: 'Quantity cannot drop to zero — delete the product.',
  quantity_at_max: 'Maximum quantity reached.',
  meals_stale: 'The section list is out of date — reload the page.',
  product_name_required: `Enter a product name (max ${NAME_MAX} chars).`,
  meal_name_required: `Enter a section name (max ${NAME_MAX} chars).`,
  grams_invalid: `Grams must be a whole number from 1 to ${GRAMS_MAX}.`,
  kcal_invalid: `kcal/100g must be a whole number from 0 to ${KCAL_PER_100G_MAX}.`,
  quantity_invalid: `Quantity must be a number from 0.01 to ${QUANTITY_MAX} (at most 2 decimal places).`,
  daily_limit_invalid: `The limit must be a whole number from ${DAILY_LIMIT_MIN} to ${DAILY_LIMIT_MAX}.`,
  day_invalid: 'Enter a date (YYYY-MM-DD).',
  weight_invalid: `The weight must be a number from ${WEIGHT_MIN} to ${WEIGHT_MAX} kg, with at most one decimal place.`,
  weight_not_found: 'Measurement not found.',
  weight_date_taken: 'That day already has a weight measurement.',
  recipe_not_found: 'Recipe not found.',
  duplicate_recipe: 'You already have a recipe with this name.',
  checklist_not_found: 'List not found.',
  checklist_item_not_found: 'Item not found on this list.',
  duplicate_checklist: 'You already have a list with this name.',
  checklist_child_invalid:
    'A combined list can only include your own standard lists.',
  checklist_children_required: 'Pick at least one list.',
  checklist_group_not_found: 'Group not found.',
  duplicate_checklist_group: 'You already have a group with this name.',
  checklist_group_not_empty:
    'The group still holds lists — move them to another group first.',
  checklist_group_invalid: 'A list can only be saved into one of your groups.',
  checklist_order_stale: 'The order is out of date — reload the page.',
};

export const errors: Dictionary<ErrorTexts> = { pl, en };

/**
 * An error code as text in the screen's current language. Takes a plain
 * string so a client-side `safeParse` issue message can go straight in; one
 * that is not a known code reads as `invalid_request`.
 */
export function translateError(code: string): string {
  const texts = errors[currentLocale()];
  return code in texts ? texts[code as ErrorCode] : texts.invalid_request;
}
